import React from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch } from 'react-router';
import { push } from 'connected-react-router';
import { ThemeProvider } from 'styled-components';
import { AnimatedLogo, baseTheme } from '@experian-uk/corvetteuk-common-ui';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import get from 'lodash/get';
import getCustomer from '../actions/auth/getCustomer';
import getBilling from '../actions/billing/getBilling';
import getBillingProvider from '../actions/billing/getBillingProvider';
import { getPaymentMethods } from '../actions/billing/paymentMethods';
import getAllSubscriptions from '../actions/subscriptions/getAll';
import getAllOffers from '../actions/offers/getAll';
import getAlertCount from '../actions/alerts/getCount';
import getProductDetails from '../actions/product/getDetails';
import getBoostStatus from '../actions/boost/getStatus';
import getScore from '../actions/scores/getScore';
import getLockStatusIfCE from '../actions/creditLock/getStatus';
import getSelections from '../actions/conductrics/getSelections';
import getIsFlexUser from '../services/responseUtility/getIsFlexUser';
import { subscriptionSchema } from '../schemas';
import { products, descriptors } from '../constants';
import { flexProductFamilies } from '../constants/products';
import NavigationWrapper from '../components/NavigationWrapper';
import Footer from '../components/Footer';
import { getSplitStatus } from '../reducers/selectors';
import { getOfferById } from '../reducers/offers';
import ProtectedRoute from '../components/ProtectedRoute';
import ScrollToTop from './scrollToTop';
import { FullPageContainer } from '../components/Container';
import HomePage from './home';
import ConfirmPage from './confirm';
import WhichProductPage from './which-product';
import splitsList from '../constants/splits';
import getCurrentSubscriptionPrice from '../reducers/selectors/currentSubscriptionPrice';
import ThanksLanding from './thanks/landing';
import SwitchPage from './switch';
import WebSwitchPage from './switch/migration';
import BILLING_PROVIDERS from '../constants/billingProviders';
import getExperienceApi from '../actions/experienceApi/getExperienceApi';
import { EnvProvider, EnvConsumer } from '../components/Context/env';
import {
  selectEntitlements,
  selectEntitlementsError,
  selectEntitlementsLoading,
  selectIsFlexUserError,
  selectIsFlexUserLoading,
  selectIsFlexUser,
  selectProducts,
  selectProductsError,
  selectProductsLoading,
} from '../services/responseUtility/entitlementsSelectors';
import PaymentandBilling from './payment-and-billing';
import ChooserPage from './chooser';
import RestartPage from './chooser-restart';
import GuardedRoute from '../components/GuardedRoute';
import ChangeBillingDatePage from './change-billing-date';
import MySubscription from './my-subscription/index';
import Deactivate from '../pages/deactivate';
import FlexDataLoader from '../components/FlexDataLoader';
import CancelSubscription from './cancel-subscription/index';
import IsFlexUserLoader from '../components/IsFlexUserLoader';
import setAnalyticsPlatformChannel from '../actions/analyticsPlatform/setChannel';

@withRouter
@connect(
  state => ({
    customerId: state.auth.currentUser?.customerId,
    currentSubscription: state.subscriptions.current,
    currentSubscriptionPrice: getCurrentSubscriptionPrice(state),
    isDeferred: !!state.subscriptions.current?.defermentInfo,
    offers: state.offers,
    creditExpertOffer: getOfferById(state.offers, get(state.offers[products.credit_expert], 'offerId')),
    productDetails: state.productDetails,
    query: state.router.location.query,
    splitsReady: state.split.fetched || state.split.error || state.auth.error,
    directDebitEnabled: getSplitStatus(state.split, [splitsList.prodmoveDD])[splitsList.prodmoveDD],
    isNewDesignEnabled: getSplitStatus(state.split, [splitsList.prodmoveNewDesign])[splitsList.prodmoveNewDesign],
    billingProvider: state.billing.billingProvider,
    hasCBDEarliestSelectableDate: !!state.billing?.dateChangeEligibility?.data?.earliestSelectableDate,
    isFlexUser: selectIsFlexUser(state),
    isFlexUserError: selectIsFlexUserError(state),
    isFlexUserLoading: selectIsFlexUserLoading(state),
    entitlements: selectEntitlements(state),
    entitlementsError: selectEntitlementsError(state),
    entitlementsLoading: selectEntitlementsLoading(state),
    products: selectProducts(state),
    productsError: selectProductsError(state),
    productsLoading: selectProductsLoading(state),
  }),
  dispatch =>
    bindActionCreators(
      {
        getCustomer,
        getIsFlexUser,
        getBilling,
        getBillingProvider,
        getAllSubscriptions,
        getPaymentMethods,
        getProductDetails,
        getAllOffers,
        getAlertCount,
        getBoostStatus,
        getLockStatusIfCE,
        getSelections,
        getExperienceApi,
        getScore,
        setAnalyticsPlatformChannel,
        push,
      },
      dispatch
    )
)
export default class DefaultLayout extends React.Component {
  static propTypes = {
    getCustomer: PropTypes.func.isRequired,
    getIsFlexUser: PropTypes.func.isRequired,
    getBilling: PropTypes.func.isRequired,
    getBillingProvider: PropTypes.func.isRequired,
    getAllSubscriptions: PropTypes.func.isRequired,
    getScore: PropTypes.func.isRequired,
    getPaymentMethods: PropTypes.func.isRequired,
    getAlertCount: PropTypes.func.isRequired,
    getBoostStatus: PropTypes.func.isRequired,
    getLockStatusIfCE: PropTypes.func.isRequired,
    currentSubscription: PropTypes.shape(subscriptionSchema),
    currentSubscriptionPrice: PropTypes.number,
    getAllOffers: PropTypes.func.isRequired,
    offers: PropTypes.shape({
      isLoaded: PropTypes.bool,
    }),
    creditExpertOffer: PropTypes.shape({
      terms: PropTypes.arrayOf(
        PropTypes.shape({
          price: PropTypes.number,
        })
      ),
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }).isRequired,
    query: PropTypes.shape({
      switchingTo: PropTypes.string,
    }),
    getProductDetails: PropTypes.func.isRequired,
    splitsReady: PropTypes.bool,
    directDebitEnabled: PropTypes.bool,
    getSelections: PropTypes.func.isRequired,
    getExperienceApi: PropTypes.func.isRequired,
    setAnalyticsPlatformChannel: PropTypes.func.isRequired,
    isDeferred: PropTypes.bool,
    billingProvider: PropTypes.string,
    isNewDesignEnabled: PropTypes.bool,
    push: PropTypes.func.isRequired,
    customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hasCBDEarliestSelectableDate: PropTypes.bool,
    isFlexUser: PropTypes.bool,
    isFlexUserError: PropTypes.bool,
    isFlexUserLoading: PropTypes.bool,
    entitlements: PropTypes.any,
    entitlementsLoading: PropTypes.bool,
    entitlementsError: PropTypes.bool,
    products: PropTypes.any,
    productsLoading: PropTypes.bool,
    productsError: PropTypes.bool,
  };

  static defaultProps = {
    currentSubscription: {},
    currentSubscriptionPrice: undefined,
    offers: {
      isLoaded: false,
    },
    creditExpertOffer: {},
    query: {},
    directDebitEnabled: false,
    splitsReady: false,
    isDeferred: false,
    billingProvider: null,
    isNewDesignEnabled: false,
    hasCBDEarliestSelectableDate: false,
    isFlexUser: false,
    isFlexUserError: false,
    isFlexUserLoading: true,
    entitlements: null,
    entitlementsLoading: true,
    entitlementsError: false,
    products: null,
    productsLoading: true,
    productsError: false,
  };

  state = {
    ready: !['switch', 'chooser', 'billing'].some(segment => this.props.location.pathname.includes(segment)),
  };

  componentDidMount() {
    void this.loadInitialData();
  }

  async loadInitialData() {
    /* Check we can establish a users billing provider before continuing, this will redirect to an error
    screen if the provider cannot be established */
    try {
      await this.props.setAnalyticsPlatformChannel();
      await this.props.getBillingProvider();
      const { offers } = this.props;
      const getOffers = () => (!offers.isLoaded ? this.props.getAllOffers() : Promise.resolve());

      const getSubscriptionDependants = async () => {
        await this.props.getAllSubscriptions();
        await this.props.getScore();
        await this.props.getLockStatusIfCE();
      };

      Promise.all([
        this.props.getCustomer(),
        this.props.getBilling(),
        this.props.getPaymentMethods(),
        this.props.getProductDetails(),
        this.props.getBoostStatus(),
        getSubscriptionDependants(),
        getOffers(),
      ]).finally(() => {
        this.props.getSelections();
        this.props.getExperienceApi();
        this.setState({
          ready: true,
        });
      });

      this.props.getAlertCount();
    } catch (error) {
      this.props.push('/error/provider');
    }
  }

  render() {
    const { creditExpertOffer, currentSubscription, location, query, currentSubscriptionPrice, isDeferred } =
      this.props;

    const { switchingTo } = query;
    const currentLocation = {
      ...location,
      switchingTo,
    };
    const customerDataLoaded = !!this.props.customerId;

    const {
      isNewDesignEnabled,
      isFlexUser,
      isFlexUserLoading,
      entitlements,
      entitlementsLoading,
      products,
      productsLoading,
    } = this.props;

    const flexDetailsReady = !entitlementsLoading && !productsLoading && !!entitlements && !!products;
    const flexDependenciesReady = !isNewDesignEnabled || (!isFlexUserLoading && (!isFlexUser || flexDetailsReady));
    // Breach/BreachPlus flex users keep the legacy HomePage experience
    const isBreachFamily = [flexProductFamilies.breach, flexProductFamilies.breachPlus].includes(entitlements?.family);
    const showMySubscription = isFlexUser && isNewDesignEnabled && !isBreachFamily;

    const dependenciesReady =
      this.state.ready && this.props.billingProvider && this.props.splitsReady && flexDependenciesReady;

    const hasFlexDependencyError =
      this.props.isNewDesignEnabled &&
      (this.props.isFlexUserError ||
        (this.props.isFlexUser && (this.props.entitlementsError || this.props.productsError)));

    if (hasFlexDependencyError) {
      return <Redirect to="/error/provider" />;
    }

    const endDt = this.props.isFlexUser ? this.props.entitlements?.endDt : this.props.currentSubscription?.endDt;
    const hasPendingDeactivation = !!endDt && new Date(endDt) > new Date();

    const hasActiveSubscription = entitlements?.active === true || currentSubscription?.active === true;

    const content = dependenciesReady ? (
      <ThemeProvider theme={baseTheme}>
        <ScrollToTop>
          <EnvProvider>
            <EnvConsumer>
              {env => (
                <React.Fragment>
                  <NavigationWrapper />
                  <Switch>
                    <Route exact path="/" component={showMySubscription ? MySubscription : HomePage} />
                    <Route
                      exact
                      path="/my-subscription"
                      render={() => (this.props.isNewDesignEnabled ? <HomePage /> : <Redirect to="/" />)}
                    />
                    <GuardedRoute
                      exact
                      path="/cancel"
                      component={CancelSubscription}
                      canActivate={hasActiveSubscription}
                      redirectTo="/"
                    />
                    <Route exact path="/chooser" component={ChooserPage} />
                    <Route exact path="/chooser/restart" component={RestartPage} />
                    <GuardedRoute
                      exact
                      path="/deactivate"
                      render={props => (
                        <Deactivate {...props} useFlexFlow={this.props.isFlexUser && this.props.isNewDesignEnabled} />
                      )}
                      canActivate={!hasPendingDeactivation}
                      redirectTo="/"
                    />
                    <Route
                      exact
                      path="/switch"
                      component={this.props.billingProvider === BILLING_PROVIDERS.NEW ? WebSwitchPage : SwitchPage}
                    />
                    <Route exact path="/thanks" component={ThanksLanding} />
                    <GuardedRoute
                      exact
                      path="/billing"
                      component={PaymentandBilling}
                      canActivate={hasActiveSubscription}
                      redirectTo="/"
                    />
                    <GuardedRoute
                      exact
                      path="/billing/change-date"
                      render={props => <ChangeBillingDatePage {...props} isWebview={false} />}
                      canActivate={hasActiveSubscription && this.props.hasCBDEarliestSelectableDate}
                      redirectTo={hasActiveSubscription ? '/billing' : '/'}
                    />
                    <Route exact path="/confirm">
                      {this.props.directDebitEnabled ? <ConfirmPage /> : <Redirect to="/" />}
                    </Route>
                    <ProtectedRoute
                      exact
                      path="/our-products/:returnUrl?"
                      component={WhichProductPage}
                      allowedDescriptors={[descriptors.free]}
                    />
                    <Redirect from="*" to="/" />
                  </Switch>
                  <Footer
                    creditExpertOffer={creditExpertOffer}
                    currentLocation={currentLocation}
                    currentSubscriptionFamily={currentSubscription.family}
                    productPrice={currentSubscriptionPrice}
                    urls={{
                      experianUrl: env.REACT_APP_EXPERIAN_URL,
                      insUrl: env.REACT_APP_INS_URL,
                    }}
                    isDeferred={isDeferred}
                  />
                </React.Fragment>
              )}
            </EnvConsumer>
          </EnvProvider>
        </ScrollToTop>
      </ThemeProvider>
    ) : (
      <FullPageContainer data-automation-test-element="default-routes-loading">
        <AnimatedLogo />
      </FullPageContainer>
    );

    return (
      <React.Fragment>
        {customerDataLoaded && <IsFlexUserLoader />}
        {customerDataLoaded && this.props.splitsReady && <FlexDataLoader />}
        {content}
      </React.Fragment>
    );
  }
}
